import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import PatientList from '../components/admin/PatientList';
import PatientDetail from '../components/admin/PatientDetail';
import Analytics from '../components/admin/Analytics';
import ConsentFormsList from '../components/admin/ConsentFormsList';
import AdminNavbar from '../components/admin/AdminNavbar';
import api from '../utils/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const { user, loading: authLoading, hasAnyRole } = useAuth();
    const [activeTab, setActiveTab] = useState('patients');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [accessError, setAccessError] = useState(null);

    // ── Cycle pricing state ──────────────────────────────────────────────────
    const [pricingConfig, setPricingConfig] = useState(null);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [pricingForm, setPricingForm] = useState({ isTrialEnabled: true, priceInCents: 999, currency: 'usd', description: '' });
    const [pricingSaving, setPricingSaving] = useState(false);
    const [pricingMsg, setPricingMsg] = useState(null); // { type: 'success'|'error', text }

    const loadPricingConfig = async () => {
        setPricingLoading(true);
        try {
            const { data } = await api.get('/admin/cycle-pricing');
            const cfg = data.data;
            setPricingConfig(cfg);
            setPricingForm({ isTrialEnabled: cfg.isTrialEnabled, priceInCents: cfg.priceInCents, currency: cfg.currency, description: cfg.description || '' });
        } catch {
            setPricingMsg({ type: 'error', text: 'Failed to load pricing config.' });
        } finally {
            setPricingLoading(false);
        }
    };

    const savePricingConfig = async (e) => {
        e.preventDefault();
        setPricingSaving(true);
        setPricingMsg(null);
        try {
            const payload = {
                isTrialEnabled: pricingForm.isTrialEnabled,
                priceInCents: parseInt(pricingForm.priceInCents, 10),
                currency: pricingForm.currency.toLowerCase().trim(),
                description: pricingForm.description,
            };
            const { data } = await api.put('/admin/cycle-pricing', payload);
            setPricingConfig(data.data);
            setPricingMsg({ type: 'success', text: 'Pricing settings saved successfully.' });
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save pricing config.';
            setPricingMsg({ type: 'error', text: msg });
        } finally {
            setPricingSaving(false);
        }
    };

    // Check access on mount and when auth state changes
    useEffect(() => {
        if (!authLoading) {
            if (!user) {
                setAccessError('Authentication required');
                return;
            }

            if (!hasAnyRole(['admin', 'doctor'])) {
                setAccessError('Insufficient privileges. Admin or Doctor access required.');
                return;
            }

            // Clear any previous access errors
            setAccessError(null);
            loadPatients();
        }
    }, [authLoading, user, hasAnyRole]);

    useEffect(() => {
        if (activeTab === 'payments' && !pricingConfig && !pricingLoading) {
            loadPricingConfig();
        }
    }, [activeTab]);  // eslint-disable-line react-hooks/exhaustive-deps

    const loadPatients = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/patients');
            setPatients(response.data);
        } catch (error) {
            console.error('Error loading patients:', error);
            if (error.response?.status === 403) {
                setAccessError('Access denied. You do not have permission to view patient data.');
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredPatients = patients.filter(patient => {
        const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            patient.email.toLowerCase().includes(searchTerm.toLowerCase());

        if (filterStatus === 'all') return matchesSearch;

        const hasRecentActivity = patient.lastJournalEntry &&
            new Date(patient.lastJournalEntry) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

        if (filterStatus === 'active') return matchesSearch && hasRecentActivity;
        if (filterStatus === 'inactive') return matchesSearch && !hasRecentActivity;

        return matchesSearch;
    });

    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
        setActiveTab('patient-detail');
    };

    const handleBackToList = () => {
        setSelectedPatient(null);
        setActiveTab('patients');
    };

    // Debug: Log user data to check role
    console.log('Admin Dashboard - User:', user);
    console.log('Auth loading:', authLoading);

    // Show loading while auth is loading
    if (authLoading) {
        return (
            <div className="admin-dashboard">
                <div className="access-denied">
                    <div className="loading-spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    // Show access error if there's one
    if (accessError) {
        const redirectPath = user?.role === 'patient' ? '/dashboard' : '/login';
        return (
            <div className="admin-dashboard">
                <div className="access-denied">
                    <h2>Access Denied</h2>
                    <p>{accessError}</p>
                    <p>Current role: {user?.role || 'Not logged in'}</p>
                    <button onClick={() => window.location.href = redirectPath}>
                        Go to {user?.role === 'patient' ? 'Dashboard' : 'Login'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <AdminNavbar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedPatient={selectedPatient}
                onBackToList={handleBackToList}
            />

            <main className="admin-main-content">
                {activeTab === 'patients' && (
                    <div className="patients-section">
                        <div className="section-header">
                            <h1>Patient Management</h1>
                            <div className="patients-stats">
                                <div className="stat-card">
                                    <span className="stat-number">{patients.length}</span>
                                    <span className="stat-label">Total Patients</span>
                                </div>
                                <div className="stat-card">
                                    <span className="stat-number">
                                        {patients.filter(p => p.lastJournalEntry &&
                                            new Date(p.lastJournalEntry) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                                        ).length}
                                    </span>
                                    <span className="stat-label">Active This Week</span>
                                </div>
                                <div className="stat-card">
                                    <span className="stat-number">
                                        {patients.filter(p => p.journalProgress && p.journalProgress >= 50).length}
                                    </span>
                                    <span className="stat-label">50%+ Complete</span>
                                </div>
                            </div>
                        </div>

                        <div className="patients-controls">
                            <div className="search-filter-bar">
                                <input
                                    type="text"
                                    placeholder="Search patients by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Patients</option>
                                    <option value="active">Active (7 days)</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <PatientList
                            patients={filteredPatients}
                            loading={loading}
                            onPatientSelect={handlePatientSelect}
                            onRefresh={loadPatients}
                        />
                    </div>
                )}

                {activeTab === 'patient-detail' && selectedPatient && (
                    <PatientDetail
                        patient={selectedPatient}
                        onBack={handleBackToList}
                        onRefresh={loadPatients}
                    />
                )}

                {activeTab === 'analytics' && (
                    <Analytics patients={patients} />
                )}

                {activeTab === 'consents' && (
                    <ConsentFormsList />
                )}

                {activeTab === 'payments' && (
                    <div className="payments-section">
                        <div className="section-header">
                            <h1>Cycle Payment Settings</h1>
                            <p className="section-subtitle">Control trial availability and per-cycle pricing charged to patients.</p>
                        </div>

                        {pricingLoading && <p className="pricing-loading">Loading settings…</p>}

                        {!pricingLoading && (
                            <form className="pricing-form" onSubmit={savePricingConfig}>

                                <div className="pricing-card">
                                    <h3>Trial Cycle</h3>
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={pricingForm.isTrialEnabled}
                                            onChange={(e) => setPricingForm(f => ({ ...f, isTrialEnabled: e.target.checked }))}
                                        />
                                        <span className="toggle-text">
                                            {pricingForm.isTrialEnabled ? 'Cycle 1 is FREE for all new patients' : 'No free trial — all cycles require payment'}
                                        </span>
                                    </label>
                                </div>

                                <div className="pricing-card">
                                    <h3>Per-Cycle Price</h3>
                                    <div className="pricing-row">
                                        <div className="pricing-field">
                                            <label>Amount (in smallest currency unit)</label>
                                            <input
                                                type="number"
                                                min="50"
                                                value={pricingForm.priceInCents}
                                                onChange={(e) => setPricingForm(f => ({ ...f, priceInCents: e.target.value }))}
                                                className="pricing-input"
                                            />
                                            <small>e.g. 999 = $9.99 USD &nbsp;|&nbsp; 79900 = ₹799 INR</small>
                                        </div>
                                        <div className="pricing-field">
                                            <label>Currency (3-letter ISO code)</label>
                                            <input
                                                type="text"
                                                maxLength={3}
                                                placeholder="usd"
                                                value={pricingForm.currency}
                                                onChange={(e) => setPricingForm(f => ({ ...f, currency: e.target.value.toLowerCase() }))}
                                                className="pricing-input pricing-input--currency"
                                            />
                                            <small>usd · eur · gbp · inr · cad · aud · sgd · jpy…</small>
                                        </div>
                                    </div>
                                </div>

                                <div className="pricing-card">
                                    <h3>Payment Description</h3>
                                    <input
                                        type="text"
                                        maxLength={200}
                                        placeholder="Start a new health journal cycle"
                                        value={pricingForm.description}
                                        onChange={(e) => setPricingForm(f => ({ ...f, description: e.target.value }))}
                                        className="pricing-input pricing-input--full"
                                    />
                                    <small>Shown on the Stripe payment receipt.</small>
                                </div>

                                {pricingMsg && (
                                    <p className={`pricing-msg pricing-msg--${pricingMsg.type}`}>{pricingMsg.text}</p>
                                )}

                                <div className="pricing-actions">
                                    <button type="submit" className="btn-save-pricing" disabled={pricingSaving}>
                                        {pricingSaving ? 'Saving…' : 'Save Changes'}
                                    </button>
                                    <button type="button" className="btn-reload-pricing" onClick={loadPricingConfig} disabled={pricingLoading}>
                                        Reload
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminDashboard;